import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, XCircle, Send, FileCode } from 'lucide-react';

const STATUS_COLOR = {
  draft:                  'text-cyan-sys border-cyan-sys',
  approved:               'text-buy border-buy',
  rejected:               'text-sell border-sell',
  rejected_out_of_scope:  'text-sell border-sell',
};

/**
 * EngineeringPlanner — Phase 51.B Lab section.
 * Operator types a feature request → AI returns a structured plan.
 * Plan can be approved / rejected. Approved plans can spawn a CodeDiff.
 */
export default function EngineeringPlannerSection({ isProd, onSwitchTab }) {
  const [plans, setPlans]       = useState([]);
  const [request, setRequest]   = useState('');
  const [busy, setBusy]         = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [generatingDiff, setGeneratingDiff] = useState(null);

  const load = () =>
    api.get('/lab/planner/list?limit=50').then(({ data }) => setPlans(data.plans || [])).catch(() => {});

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!request.trim() || busy) return;
    setBusy(true);
    try {
      await api.post('/lab/planner/create', { request });
      setRequest('');
      toast.success('Plan generated');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Plan failed');
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (id, action) => {
    try {
      await api.post(`/lab/planner/${id}/${action}`);
      toast.success(`Plan ${action}d`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${action} failed`);
    }
  };

  const generateDiff = async (id) => {
    setGeneratingDiff(id);
    try {
      const { data } = await api.post(`/lab/codediff/generate/${id}`);
      toast.success(`Diff generated · ${data.diffs?.length || 0} files`);
      onSwitchTab?.('codediffs');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Diff generation failed');
    } finally {
      setGeneratingDiff(null);
    }
  };

  const selected = plans.find((p) => p.id === selectedId);

  return (
    <div data-testid="lab-planner" className="space-y-3">
      {/* INPUT */}
      <div className="border border-cyan-sys p-3">
        <div className="text-[10px] tracking-[0.25em] uppercase text-cyan-sys mb-2">// new feature request</div>
        <textarea
          data-testid="lab-planner-input"
          value={request}
          onChange={(e) => setRequest(e.target.value)}
          placeholder='e.g. "Add an Options Flow panel showing unusual options activity, with filters for ticker, expiry, and volume threshold."'
          className="w-full bg-transparent border border-[var(--border-default)] px-2 py-2 text-[12px] font-mono h-24 focus:border-cyan-sys outline-none"
          disabled={!isProd || busy}
        />
        <div className="flex items-center justify-between mt-2">
          <div className="text-[10px] text-[var(--txt-secondary)]">
            {isProd
              ? 'Plan generation calls Claude Sonnet 4.5 grounded in the live architecture index. Costs ~$0.02/plan.'
              : '⚠ Planner is production-only. Write actions disabled in preview.'}
          </div>
          <button
            data-testid="lab-planner-submit"
            onClick={create}
            disabled={!isProd || busy || !request.trim()}
            className="flex items-center gap-1.5 border border-cyan-sys text-cyan-sys px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] hover:bg-cyan-sys hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
            {busy ? 'planning...' : 'generate plan'}
          </button>
        </div>
      </div>

      {/* PLAN LIST */}
      <div className="grid md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--txt-secondary)] mb-1">
            {plans.length} plans
          </div>
          {plans.length === 0 ? (
            <div className="border border-[var(--border-default)] p-4 text-[11px] text-[var(--txt-secondary)]">
              No plans yet. Submit a feature request above to generate the first one.
            </div>
          ) : (
            plans.map((p) => (
              <button
                key={p.id}
                data-testid={`lab-plan-${p.id}`}
                onClick={() => setSelectedId(p.id)}
                className={`w-full text-left border p-2.5 transition-colors ${
                  p.id === selectedId
                    ? 'border-cyan-sys bg-[var(--bg-panel-lighter)]'
                    : 'border-[var(--border-default)] hover:border-cyan-sys'
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <span className="text-[11px] text-[var(--txt-primary)] truncate">
                    {p.plan?.goal || p.request.slice(0, 80)}
                  </span>
                  <span className={`text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 border ${STATUS_COLOR[p.status] || 'text-[var(--txt-muted)] border-[var(--border-default)]'}`}>
                    {p.status}
                  </span>
                </div>
                <div className="text-[9px] text-[var(--txt-secondary)] truncate">
                  {(p.created_at || '').slice(0, 19).replace('T', ' ')}Z · {p.created_by}
                  {p.plan?.complexity && <> · complexity={p.plan.complexity}</>}
                  {p.plan?.estimated_hours != null && <> · ~{p.plan.estimated_hours}h</>}
                </div>
              </button>
            ))
          )}
        </div>

        {/* PLAN DETAIL */}
        <div className="border border-[var(--border-default)] p-3 min-h-[400px]">
          {!selected ? (
            <div className="text-[11px] text-[var(--txt-secondary)]">Select a plan to view details.</div>
          ) : (
            <div className="space-y-3 text-[11px]">
              <div>
                <div className="text-[9px] tracking-[0.25em] uppercase text-cyan-sys mb-1">// operator request</div>
                <div className="text-[var(--txt-primary)] whitespace-pre-wrap">{selected.request}</div>
              </div>

              {selected.plan?.error ? (
                <div className="border border-sell text-sell p-2 text-[11px]">
                  Out of scope: {selected.plan.reason}
                </div>
              ) : selected.plan ? (
                <PlanBody plan={selected.plan} />
              ) : null}

              <div className="flex flex-wrap gap-1.5 pt-2 border-t border-[var(--border-default)]">
                {selected.status === 'draft' && (
                  <>
                    <button
                      data-testid="lab-plan-approve"
                      onClick={() => setStatus(selected.id, 'approve')}
                      disabled={!isProd}
                      className="flex items-center gap-1 border border-buy text-buy px-2 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-buy hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <CheckCircle2 size={11} /> approve
                    </button>
                    <button
                      data-testid="lab-plan-reject"
                      onClick={() => setStatus(selected.id, 'reject')}
                      disabled={!isProd}
                      className="flex items-center gap-1 border border-sell text-sell px-2 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-sell hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <XCircle size={11} /> reject
                    </button>
                  </>
                )}
                {(selected.status === 'approved' || selected.status === 'draft') && !selected.plan?.error && (
                  <button
                    data-testid="lab-plan-generate-diff"
                    onClick={() => generateDiff(selected.id)}
                    disabled={!isProd || generatingDiff === selected.id}
                    className="flex items-center gap-1 border border-cyan-sys text-cyan-sys px-2 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-cyan-sys hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    {generatingDiff === selected.id
                      ? <Loader2 size={11} className="animate-spin" />
                      : <FileCode size={11} />}
                    {generatingDiff === selected.id ? 'generating...' : 'generate code diff'}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PlanBody({ plan }) {
  return (
    <div className="space-y-2.5">
      <Field k="goal"     v={plan.goal} mono={false} />
      <Field k="complexity" v={`${plan.complexity || '?'} · ~${plan.estimated_hours ?? '?'}h`} />

      {plan.files_to_create?.length > 0 && (
        <Field k="new files" v={plan.files_to_create} list />
      )}
      {plan.files_to_modify?.length > 0 && (
        <Field k="modified files" v={plan.files_to_modify} list />
      )}
      {plan.api_changes?.length > 0 && (
        <Field k="api changes" v={plan.api_changes.map(
          (a) => `${a.method} ${a.path} (${a.change}) — ${a.purpose}`
        )} list />
      )}
      {plan.schema_changes?.length > 0 && (
        <Field k="schema changes" v={plan.schema_changes.map(
          (s) => `${s.collection} (${s.change}) — ${s.details}`
        )} list />
      )}
      {plan.ui_changes?.length > 0 && (
        <Field k="ui changes" v={plan.ui_changes} list />
      )}
      {plan.risks?.length > 0 && (
        <Field k="risks" v={plan.risks} list color="text-hold" />
      )}
      {plan.tests_required?.length > 0 && (
        <Field k="tests required" v={plan.tests_required} list />
      )}
      {plan.breaking_changes?.length > 0 && (
        <Field k="breaking changes" v={plan.breaking_changes} list color="text-sell" />
      )}
      {plan.rollback_path && (
        <Field k="rollback path" v={plan.rollback_path} />
      )}
    </div>
  );
}

function Field({ k, v, list, mono = true, color }) {
  return (
    <div>
      <div className="text-[9px] tracking-[0.25em] uppercase text-cyan-sys mb-0.5">// {k}</div>
      {list ? (
        <ul className={`space-y-0.5 ${mono ? 'font-mono text-[10px]' : 'text-[11px]'} ${color || ''}`}>
          {v.map((item, i) => (
            <li key={i} className="text-[var(--txt-primary)]">· {String(item)}</li>
          ))}
        </ul>
      ) : (
        <div className={`${mono ? 'font-mono text-[10px]' : 'text-[11px]'} text-[var(--txt-primary)] ${color || ''}`}>
          {String(v ?? '—')}
        </div>
      )}
    </div>
  );
}
